import React, { useEffect, useRef, useState } from "react";
import { MapContainer, Tooltip, useMap } from 'react-leaflet';
import { GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css'
import { TileLayer } from "react-leaflet";
import { colorManipulationToBright, getPropertyByPath } from "../../config/utilities";
import useAutoZoom from "../../config/customHooks";
import { handleMultipleKeys } from "../../config/utilities";
import { Col, Row } from "react-bootstrap";
import { useInView } from 'react-intersection-observer';
import "@fortawesome/fontawesome-free/css/all.min.css";



const AddResetButton = ({ center, zoom, mapKey, mapInView, controls }) => {
    const map = useMap();
    const resetButtonPlaced = useRef(false);

    useEffect(() => {

      if(mapInView && !resetButtonPlaced.current){
        
        const mapElement = document.getElementById(`map-id-${mapKey}`);
        
        if(mapElement){

            const zoomControls = mapElement.getElementsByClassName("leaflet-control");
            
            if(zoomControls.length > 0){

                if(!controls){
                    zoomControls[0].getElementsByClassName("leaflet-control-zoom-in")[0].style.display = "none";
                    zoomControls[0].getElementsByClassName("leaflet-control-zoom-out")[0].style.display = "none";
                }
                
                const resetButton = document.createElement("a");
                resetButton.className = "leaflet-control-zoom-reset";
                resetButton.href = "#";
                resetButton.title = "Reset Zoom";
                resetButton.role = "button";
                resetButton.ariaLabel = "Reset Zoom";
                resetButton.ariaDisabled = "false";
                resetButton.onclick = (e) => {
                    e.preventDefault();
                    map.setView(center, zoom);
                };

                const spanButton = document.createElement("span");

                spanButton.className = "fa-solid fa-location-crosshairs"; 

                resetButton.appendChild(spanButton);

                zoomControls[0].appendChild(resetButton);
                resetButtonPlaced.current = true;
            }
        }
      }
    }, [map, mapInView, mapKey, center, zoom, controls]);
  
    return null;
};

const MapWidget = (
    {
        mapKey = "defaultMap",
        mapState = [], 
        mapCenter = [], 
        loadingData = false,
        resetPosition = false,
        tooltipTitlesShow = true,
        locale = "en-UK",
        tileLayerUrl = "",
        polygonID = "id",
        draggable = true,
        zoom = {
            level: 0, 
            max: 10, 
            min: 1,
            scroll: false,
            controller: false
        },
        mapStyle = {},
        mapClass = null,
        tooltipContent = null,
        defaultMapGradient = "#00000",
        showLegend = false,
        mapGradientRange = {
            max: null,
            min: null
        },
        mapGradientSelection = {
            colors: [],
            splitLevel: 10
        },
        onClickFunction = null,
        layerMouseBaseStyle = {
            fillOpacity: 1,
            weight: 0.8,
            opacity: 1,
            dashArray: 0,
            lineColor: "white"
        },
        layerMouseOverStyle = {
            color: '#F5556F',
            fillOpacity: 1,
            weight: 1,
            opacity: 1,
            lineColor: "red"
        },
        layerMouseClickedStyle = {
            color: '#F5556F',
            fillOpacity: 1,
            weight: 1,
            opacity: 1,
            lineColor: "blue"
        },
        externalContent = [],
        externalContentId = "id",
        propertiesValue = "density",
        prefix = "",
        suffix = ""
    }) => {

    const [mapLegend, inView] = useInView();
    const [mapField, mapInView] = useInView();
    const totalRef = useRef(0);

    let totValues = 0;
    let higherValue = 0;

    
    mapState.features.forEach( region => {
        if(region.properties[propertiesValue] >= 0){
            totValues+=region.properties[propertiesValue];
            if(region.properties[propertiesValue] > higherValue){
                higherValue = region.properties[propertiesValue];
            }
        }
    })
    totalRef.current = totValues;

    const legendComposer = (div) => {
        if(mapGradientSelection.colors.length > 1){
            
            const reverseArray = mapGradientSelection.colors.reverse();
            const arrayLenght = mapGradientSelection.colors.length;
            
            let percentage = 100 / arrayLenght;
            const singlePercentage = 100 / arrayLenght;
            let gradiation = ` ${reverseArray[0]} ${percentage}%,`;
            const endGradiation = ` ${reverseArray[arrayLenght - 1]} ${100 - percentage}%`
            
            for(let i = 1; i < (arrayLenght - 1); i++){
                gradiation = gradiation + ` ${reverseArray[i]} ${percentage}% ${percentage + singlePercentage}%,`
                percentage = percentage + singlePercentage;
            }
            
            gradiation = gradiation + endGradiation;
            div.style.background = `linear-gradient(to bottom,${gradiation})`;
        }
        else if(mapGradientRange.max){
            div.style.background = `linear-gradient(to bottom, ${mapGradientRange.max} 0%, ${mapGradientRange.min} 100%)`;
        }else{
            div.style.background = `linear-gradient(to bottom, ${defaultMapGradient} 0%, white 100%)`;
        }
        document.getElementById(`map-legend-${mapKey}`).animate(
            [
              { opacity: 0 },
              { opacity: 1 }
            ],
            {
              duration: 750,
              easing: 'ease-in-out'
            }
        );
        document.getElementById(`map-legend-${mapKey}`).style.opacity = 1;
    }
    
    useEffect(() => {
        if(showLegend){
            if((totalRef.current > 0) && inView){
                legendComposer(document.getElementById("map-legend__colors-displayer"))
            }
        }
        
    }, [totalRef, inView]);


    const avarageValues = totValues/mapState.features.length

    let estimatedCentroid = [];
    if(!mapCenter.length && mapState.features[0].properties.centroid){
        if(mapState.features.length === 1){
            estimatedCentroid = mapState.features[0].properties.centroid;
        }
        else{
            let maxX = 0;
            let maxY = 0;
            const polygons = mapState.features;
            polygons.forEach(polygon => {
                maxX = maxX + polygon.properties.centroid[0];
                maxY = maxY + polygon.properties.centroid[1];
            })
            estimatedCentroid = [(maxY / polygons.length), (maxX / polygons.length)];
        }
    }

    function getColor(value) {
        if(mapGradientRange.max && mapGradientRange.min){
            let gradiation = 100 / mapGradientSelection.splitLevel
            gradiation = Math.floor(gradiation);

            if(value >= avarageValues){
                for(let i = 100; i >= 0; i -= gradiation ){
                    if((value >= (avarageValues*(1 + (i/100))))){
                        return colorManipulationToBright((100 - i), mapGradientRange.max);
                    }
                }
            }else{
                for(let i = 100; i >= 0; i -= gradiation ){
                    if((value >= (avarageValues*(i/100)))){
                        return colorManipulationToBright((i), mapGradientRange.min);
                    }
                }
            }
        }else if(mapGradientSelection.colors.length > 0){

            const gradientBaseLevel = avarageValues / mapGradientSelection.colors.length;
            let x = 0;
            do{
                if(x === (mapGradientSelection.colors.length - 1) || value <=  (gradientBaseLevel * (x+1))){
                    return mapGradientSelection.colors[x];
                }
                x++;

            }while((x !== mapGradientSelection.colors.length))
                
        }else if(higherValue > 0){
            const opacityLevel = 100 - ((value / higherValue) * 100);
            return colorManipulationToBright((opacityLevel), defaultMapGradient);
        }else{
            return defaultMapGradient;
        }
    }
    
    const [calcZoom, setCalcZoom] = useState(0);

    function AutoZoom() {
        return useAutoZoom(mapState, zoom, setCalcZoom);
    }


    return (
        <>
        { (mapCenter.length || (estimatedCentroid.length > 0))&&
        <div className="map-wrap" ref={mapField}>
            <MapContainer
                id={`map-id-${mapKey}`}
                center={mapCenter.length ? mapCenter : estimatedCentroid}
                minZoom={zoom.min}
                maxZoom={zoom.max}
                zoom={zoom.level}
                style={mapStyle}
                zoomControl={zoom.controller || resetPosition}
                className={mapClass}
                dragging={draggable}
                doubleClickZoom={false}
                scrollWheelZoom={zoom.scroll}
            >
                <div className="map-legend" id={`map-legend-${mapKey}`}>
                    <div className="map-legend__content">
                        <Row  className="map-legend__data-container">
                            <Col className="map-legend__colors">
                                <div className="map-legend__colors-displayer" id="map-legend__colors-displayer" ref={mapLegend}/>
                                <div className="map-legend__colors__data-placeholder"/>
                            </Col>
                            <Col className="map-legend__data">
                                <div className="map-legend__data-list">
                                    <div className="map-legend__data-highest px-1">{prefix}{!loadingData ? <>{higherValue.toLocaleString(locale)}</> : <>-</>}{suffix}</div>
                                    <div style={{height:"100%"}} />
                                    <div className="map-legend__data-lowest px-1">{prefix}0{suffix}</div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>
                <AutoZoom />
                {tileLayerUrl &&
                    <TileLayer
                        url={tileLayerUrl}
                    />
                }
                {
                    resetPosition &&
                    <AddResetButton 
                        center={estimatedCentroid} 
                        zoom={calcZoom} 
                        mapKey={mapKey} 
                        mapInView={mapInView} 
                        controls={zoom.controller}
                    />
                }
                {
                    mapState.features.map( (state, x) => {

                        const calculatedColor = getColor(state.properties[propertiesValue]);

                        const runningStyle = () => {
                            if(layerMouseClickedStyle && externalContent.some(c => handleMultipleKeys(c, externalContentId) === handleMultipleKeys(mapState.features[x], polygonID))){
                                return({
                                    fillColor: layerMouseClickedStyle.color,
                                    fillOpacity: layerMouseClickedStyle.fillOpacity,
                                    weight: layerMouseClickedStyle.weight,
                                    opacity: layerMouseClickedStyle.opacity,
                                    dashArray: layerMouseClickedStyle.dashArray,
                                    color: layerMouseClickedStyle.lineColor
                                });
                            }else{
                                return({
                                    fillColor: calculatedColor,
                                    fillOpacity: layerMouseBaseStyle.fillOpacity,
                                    weight: layerMouseBaseStyle.weight,
                                    opacity: layerMouseBaseStyle.opacity,
                                    dashArray: layerMouseBaseStyle.dashArray,
                                    color: layerMouseBaseStyle.lineColor
                                });
                            }
                        }

                        return (
                            <GeoJSON
                                key={`${mapKey}_${state[polygonID]}_${x}`}
                                data={mapState.features[x]}
                                pathOptions={() => runningStyle()}
                                style="remove-border"
                                eventHandlers={{
                                    click: () => {
                                        if(onClickFunction){
                                            onClickFunction(mapState.features[x]);
                                        }
                                    },
                                    mouseover: (e) => {
                                        const layer = e.target;
                                        layer.setStyle({
                                            fillColor: layerMouseOverStyle.color,
                                            fillOpacity: layerMouseOverStyle.fillOpacity,
                                            weight: layerMouseOverStyle.weight,
                                            opacity: layerMouseOverStyle.opacity,
                                            dashArray: layerMouseOverStyle.dashArray,
                                            color: layerMouseOverStyle.lineColor
                                        });
                                        layer.bringToFront();                                    
                                    },
                                    mouseout: (e) => {
                                        const layer = e.target;
                                        layer.setStyle(() => runningStyle());
                                    }
                                }}
                            >
                            {
                                tooltipContent &&

                                <Tooltip>
                                    {
                                        tooltipContent.map(
                                            (t, i) => {
                                                let value = getPropertyByPath(mapState.features[x], t.content);

                                                if(typeof value === "number"){
                                                    value = prefix + value.toLocaleString(locale) + suffix;
                                                }else{
                                                    value = value?.toLocaleString(locale);
                                                }

                                                return(
                                                    <div key={`${x}-${i}`} className={!tooltipTitlesShow ? "text-center" : ""}>
                                                        {
                                                            tooltipTitlesShow ?
                                                            <>
                                                                {t.title} : <b>{value}</b>
                                                            </>
                                                            :
                                                            <>
                                                                {
                                                                    i > 0 ?
                                                                    <b>{value}</b>
                                                                    :
                                                                    value
                                                                }
                                                            </>
                                                        }
                                                        <br/>
                                                    </div>
                                                )
                                            }
                                        )
                                    }
                                </Tooltip>
                            }
                            </GeoJSON>
                        )
                    })
                }
            </MapContainer>
        </div>
        }
        </>
    )
}

export default MapWidget;